import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from 'react-dropzone';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sortable Image Component
const SortableImage = ({ image, index, onRemove, onUpdateCaption }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [caption, setCaption] = useState(image.caption || '');

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: image.id || index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleSaveCaption = () => {
    onUpdateCaption(index, caption);
    setIsEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="relative bg-white rounded-lg shadow-md overflow-hidden"
    >
      {/* Solo el área de la imagen es arrastrable */}
      <div 
        {...listeners}
        className="aspect-video bg-gray-100 cursor-grab active:cursor-grabbing"
      >
        <img
          src={image.url}
          alt={image.caption || `Imagen ${index + 1}`}
          className="w-full h-full object-cover pointer-events-none"
        />
      </div>
      
      <div className="p-3">
        {isEditing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Descripción de la imagen"
              className="w-full px-2 py-1 text-sm border rounded"
            />
            <div className="flex space-x-2">
              <button
                onClick={handleSaveCaption}
                className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
              >
                Guardar
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-600 mb-2">
              {image.caption || 'Sin descripción'}
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => setIsEditing(true)}
                type="button"
                className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
              >
                Editar
              </button>
              <button
                onClick={() => onRemove(index)}
                type="button"
                className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
              >
                Eliminar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Type badges */}
      <div className="absolute top-2 left-2 flex space-x-1">
        {image.is_before && (
          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">
            Antes
          </span>
        )}
        {image.is_after && (
          <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">
            Después
          </span>
        )}
      </div>
    </div>
  );
};

// Image Upload Component
const ImageUpload = ({ onImagesAdded, propertyTitle }) => {
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    
    const uploadPromises = acceptedFiles.map(async (file, index) => {
      try {
        // Upload to CDN instead of using base64
        const formData = new FormData();
        formData.append('file', file);
        formData.append('property_title', propertyTitle || 'nueva-propiedad');
        formData.append('image_index', Date.now() + index);
        
        const response = await axios.post(`${API}/cms/upload-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if (response.data.success) {
          return {
            id: Date.now() + Math.random(),
            url: response.data.url, // CDN URL
            caption: '',
            is_before: false,
            is_after: false,
            order: 0
          };
        } else {
          throw new Error('Upload failed');
        }
      } catch (error) {
        console.error('Error uploading to CDN:', error);
        // Fallback to base64 if CDN upload fails
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              id: Date.now() + Math.random(),
              url: e.target.result,
              caption: '',
              is_before: false,
              is_after: false,
              order: 0
            });
          };
          reader.readAsDataURL(file);
        });
      }
    });

    try {
      const images = await Promise.all(uploadPromises);
      onImagesAdded(images);
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setUploading(false);
    }
  }, [onImagesAdded, propertyTitle]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    multiple: true
  });

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
        isDragActive
          ? 'border-purple-500 bg-purple-50'
          : 'border-gray-300 hover:border-gray-400'
      }`}
    >
      <input {...getInputProps()} />
      {uploading ? (
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-2"></div>
          <p className="text-gray-600">Subiendo imágenes...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <p className="text-lg font-medium text-gray-900 mb-2">
            {isDragActive ? 'Suelta las imágenes aquí' : 'Arrastra imágenes aquí'}
          </p>
          <p className="text-sm text-gray-600">
            o haz clic para seleccionar archivos
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Formatos soportados: JPEG, PNG, GIF, WebP
          </p>
        </div>
      )}
    </div>
  );
};

// Property Form Component
const PropertyForm = ({ property, onSave, onCancel }) => {
  // Prepare initial data with IDs for images and videos
  const getInitialFormData = () => {
    const baseData = {
      title: '',
      description: '',
      address: '',
      neighborhood: '',
      property_type: 'piso',
      status: 'reformando',
      bedrooms: 1,
      bathrooms: 1,
      size_m2: 50,
      images: [],
      videos: [],
      publication_date: '',
      sale_date: '',
      ...property,
    };
    
    // Ensure images have IDs
    if (property?.images) {
      baseData.images = property.images.map((img, index) => ({
        ...img,
        id: img.id || `existing_img_${index}_${Math.random().toString(36).substr(2, 9)}`
      }));
    }
    
    // Ensure videos have IDs
    if (property?.videos) {
      baseData.videos = property.videos.map((video, index) => ({
        ...video,
        id: video.id || `existing_video_${index}_${Math.random().toString(36).substr(2, 9)}`
      }));
    }
    
    return baseData;
  };
  
  const [formData, setFormData] = useState(getInitialFormData);

  const [newVideo, setNewVideo] = useState({ youtube_url: '', title: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) : value
    }));
  };

  const handleImagesAdded = (newImages) => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newImages.map((img, index) => ({
        ...img,
        id: img.id || `img_${Date.now()}_${index}`
      }))]
    }));
  };

  const handleImageRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleImageCaptionUpdate = (index, caption) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? { ...img, caption } : img)
    }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setFormData(prev => {
        const images = [...prev.images];
        const oldIndex = images.findIndex((img, idx) => (img.id || idx) === active.id);
        const newIndex = images.findIndex((img, idx) => (img.id || idx) === over.id);
        
        if (oldIndex !== -1 && newIndex !== -1) {
          const reorderedImages = arrayMove(images, oldIndex, newIndex);
          return {
            ...prev,
            images: reorderedImages.map((img, index) => ({
              ...img,
              order: index
            }))
          };
        }
        
        return prev;
      });
    }
  };

  const addVideo = () => {
    if (newVideo.youtube_url) {
      setFormData(prev => ({
        ...prev,
        videos: [...prev.videos, { ...newVideo, id: Date.now() }]
      }));
      setNewVideo({ youtube_url: '', title: '' });
    }
  };

  const removeVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Información Básica</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título *
            </label>
            <input
              type="text"
              name="title"
              required
              value={formData.title}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Barrio *
            </label>
            <input
              type="text"
              name="neighborhood"
              required
              value={formData.neighborhood}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dirección *
            </label>
            <input
              type="text"
              name="address"
              required
              value={formData.address}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción *
            </label>
            <textarea
              name="description"
              required
              rows={3}
              value={formData.description}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Propiedad *
            </label>
            <select
              name="property_type"
              value={formData.property_type}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="piso">Piso</option>
              <option value="casa">Casa</option>
              <option value="atico">Ático</option>
              <option value="estudio">Estudio</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado *
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="reformando">En Reforma</option>
              <option value="vendiendo">En Venta</option>
              <option value="reservado">Reservado</option>
              <option value="vendido">Vendido</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Habitaciones *
            </label>
            <input
              type="number"
              name="bedrooms"
              min="1"
              required
              value={formData.bedrooms}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Baños *
            </label>
            <input
              type="number"
              name="bathrooms"
              min="1"
              required
              value={formData.bathrooms}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Superficie (m²) *
            </label>
            <input
              type="number"
              name="size_m2"
              min="1"
              required
              value={formData.size_m2}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de Publicación *
            </label>
            <input
              type="date"
              name="publication_date"
              required
              value={formData.publication_date ? formData.publication_date.split('T')[0] : ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              Fecha de lanzamiento al mercado de la propiedad
            </p>
          </div>

          {formData.status === 'vendido' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Venta
              </label>
              <input
                type="date"
                name="sale_date"
                value={formData.sale_date ? formData.sale_date.split('T')[0] : ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Images Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Imágenes</h3>
        
        <div className="mb-6">
          <ImageUpload onImagesAdded={handleImagesAdded} propertyTitle={formData.title} />
        </div>

        {formData.images.length > 0 && (
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">
              Imágenes Subidas ({formData.images.length})
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Arrastra las imágenes para reordenar
            </p>
            
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={formData.images.map((img, index) => img.id || index)}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {formData.images.map((image, index) => (
                    <SortableImage
                      key={image.id || `img_key_${index}`}
                      image={image}
                      index={index}
                      onRemove={handleImageRemove}
                      onUpdateCaption={handleImageCaptionUpdate}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>

      {/* Videos Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Videos de YouTube</h3>
        
        <div className="mb-4">
          <div className="flex space-x-4">
            <div className="flex-1">
              <input
                type="url"
                placeholder="URL del video de YouTube"
                value={newVideo.youtube_url}
                onChange={(e) => setNewVideo({...newVideo, youtube_url: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                placeholder="Título del video (opcional)"
                value={newVideo.title}
                onChange={(e) => setNewVideo({...newVideo, title: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <button
              type="button"
              onClick={addVideo}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
            >
              Agregar
            </button>
          </div>
        </div>

        {formData.videos.length > 0 && (
          <div className="space-y-3">
            {formData.videos.map((video, index) => (
              <div key={video.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {video.title || `Video ${index + 1}`}
                  </p>
                  <p className="text-sm text-gray-600 truncate">
                    {video.youtube_url}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => removeVideo(index)}
                  className="ml-4 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                >
                  Eliminar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
        >
          {property?.id ? 'Actualizar' : 'Crear'} Propiedad
        </button>
      </div>
    </form>
  );
};

// Sortable Row Component for Property Table
const SortableRow = ({ property, onEdit, onDelete, getStatusColor, getStatusText }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: property.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className={`${isDragging ? 'bg-purple-50' : 'bg-white hover:bg-gray-50'} transition-colors cursor-pointer`} onClick={() => onEdit(property)}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div 
            {...attributes} 
            {...listeners} 
            className="flex-shrink-0 mr-3 cursor-move text-gray-400 hover:text-gray-600"
            title="Arrastra para reordenar"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 2a1 1 0 000 2h6a1 1 0 100-2H7zM7 8a1 1 0 000 2h6a1 1 0 100-2H7zM7 14a1 1 0 100 2h6a1 1 0 100-2H7z"></path>
            </svg>
          </div>
          <div className="h-10 w-10 flex-shrink-0">
            <img
              className="h-10 w-10 rounded-full object-cover"
              src={property.images?.[0]?.url || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=100&h=100&fit=crop'}
              alt=""
            />
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">{property.title}</div>
            <div className="text-sm text-gray-500 capitalize">{property.property_type}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">{property.neighborhood}</div>
        <div className="text-sm text-gray-500">{property.address}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(property.status)}`}>
          {getStatusText(property.status)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div>{property.bedrooms} hab • {property.bathrooms} baños</div>
        <div>{property.size_m2} m²</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div className="flex space-x-2">
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
            📷 {property.images?.length || 0}
          </span>
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
            🎥 {property.videos?.length || 0}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(property)}
            className="text-purple-600 hover:text-purple-900"
          >
            Editar
          </button>
          <button
            onClick={() => onDelete(property.id)}
            className="text-red-600 hover:text-red-900"
          >
            Eliminar
          </button>
        </div>
      </td>
    </tr>
  );
};

// Main Properties Manager Component
const PropertiesManager = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = properties.findIndex(prop => prop.id === active.id);
    const newIndex = properties.findIndex(prop => prop.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newProperties = arrayMove(properties, oldIndex, newIndex);
      setProperties(newProperties);

      // Update order on server
      await updatePropertiesOrder(newProperties);
    }
  };

  const updatePropertiesOrder = async (orderedProperties) => {
    setIsUpdatingOrder(true);
    try {
      const orderData = {
        properties: orderedProperties.map((prop, index) => ({
          property_id: prop.id,
          order: index + 1
        }))
      };

      await axios.put(`${API}/cms/properties/order`, orderData);
    } catch (error) {
      console.error('Error updating properties order:', error);
      // Revert to original order if update fails
      loadProperties();
    } finally {
      setIsUpdatingOrder(false);
    }
  };

  const handleSave = async (propertyData) => {
    try {
      // Format dates and map field names to backend
      const formattedData = {
        ...propertyData,
        start_date: propertyData.publication_date ? new Date(propertyData.publication_date).toISOString() : new Date().toISOString(),
        completion_date: propertyData.sale_date ? new Date(propertyData.sale_date).toISOString() : null,
        sale_date: null,  // This field exists in backend but different purpose
        order: propertyData.order || properties.length + 1
      };

      // Remove frontend-only fields
      delete formattedData.publication_date;

      if (editingProperty) {
        await axios.put(`${API}/cms/properties/${editingProperty.id}`, formattedData);
      } else {
        await axios.post(`${API}/cms/properties`, formattedData);
      }

      setShowForm(false);
      setEditingProperty(null);
      await loadProperties();
    } catch (error) {
      console.error('Error saving property:', error);
      alert('Error al guardar la propiedad');
    }
  };

  const handleEdit = async (property) => {
    try {
      // Load full property data with all images from backend
      const response = await axios.get(`${API}/properties/${property.id}`);
      const fullProperty = response.data;
      
      // Formatear las fechas para el formulario de edición
      const editingProperty = {
        ...fullProperty,
        // Convertir start_date (backend) a publication_date (frontend)
        publication_date: fullProperty.start_date ? fullProperty.start_date.split('T')[0] : '',
        // Mantener completion_date si existe
        sale_date: fullProperty.completion_date ? fullProperty.completion_date.split('T')[0] : ''
      };
      setEditingProperty(editingProperty);
      setShowForm(true);
    } catch (error) {
      console.error('Error loading property for edit:', error);
      alert('Error al cargar la propiedad para edición');
    }
  };

  const handleDelete = async (propertyId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta propiedad?')) {
      try {
        await axios.delete(`${API}/cms/properties/${propertyId}`);
        await loadProperties();
      } catch (error) {
        console.error('Error deleting property:', error);
        alert('Error al eliminar la propiedad');
      }
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'reformando': return 'bg-yellow-100 text-yellow-800';
      case 'vendiendo': return 'bg-green-100 text-green-800';
      case 'reservado': return 'bg-orange-100 text-orange-800';
      case 'vendido': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'reformando': return 'En Reforma';
      case 'vendiendo': return 'En Venta';
      case 'reservado': return 'Reservado';
      case 'vendido': return 'Vendido';
      default: return status;
    }
  };

  if (showForm) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {editingProperty ? 'Editar' : 'Nueva'} Propiedad
          </h1>
        </div>
        
        <PropertyForm
          property={editingProperty}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingProperty(null);
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Propiedades</h1>
          <p className="text-gray-600">Administra el portfolio de propiedades de Vivendius</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
        >
          Nueva Propiedad
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isUpdatingOrder && (
            <div className="bg-purple-50 px-4 py-2 text-sm text-purple-700 border-b border-purple-200">
              Actualizando orden de propiedades...
            </div>
          )}
          
          <p className="px-6 py-3 text-sm text-gray-600 bg-gray-50 border-b border-gray-200">
            💡 Arrastra las propiedades para reordenarlas. El orden aquí se reflejará en el sitio web.
          </p>
          
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
          <div className="overflow-x-auto">
            <table className="min-w-[800px] w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Propiedad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ubicación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Características
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Multimedia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <SortableContext items={properties.map(p => p.id)} strategy={verticalListSortingStrategy}>
                <tbody className="bg-white divide-y divide-gray-200">
                  {properties.map((property) => (
                    <SortableRow
                      key={property.id}
                      property={property}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      getStatusColor={getStatusColor}
                      getStatusText={getStatusText}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </div>
          </DndContext>
        </div>
      )}
    </div>
  );
};

export default PropertiesManager;